import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import {
  GetContractDetails,
  GetContractDetailsVariables
} from '@/graphql/contracts/_generated_/getContractDetails.query';
import {
  UpdateContractRenewal,
  UpdateContractRenewalVariables
} from '@/graphql/contracts/_generated_/updateContractRenewal.mutation';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import UpdateContractRenewalMutation from '@/graphql/contracts/updateContractRenewal.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

export default function useUpdateContractRenewal(contractId: UUID) {
  const [_updateContractRenewal, { loading, error }] =
    useMutation<UpdateContractRenewal, UpdateContractRenewalVariables>(
      UpdateContractRenewalMutation,
      {
        client: apolloClientContracts,
        onError: (err) => notificationService.open({
          title: 'Error',
          text: err.message,
          type: 'danger'
        }),
        update: (_, { data }) => {
          if (!data) return;

          const cachedContract = apolloClientContracts.readQuery<GetContractDetails, GetContractDetailsVariables>({
            query: GetContractDetailsQuery,
            variables: { contractId }
          });

          const updatedContract: GetContractDetails['contract'] = {
            ...cachedContract!.contract,
            daysBeforeRenewal: data.updateContractRenewal.daysBeforeRenewal
          };

          apolloClientContracts.writeQuery<GetContractDetails, GetContractDetailsVariables>({
            query: GetContractDetailsQuery,
            variables: { contractId },
            data: {
              contract: updatedContract
            }
          });
        },
        refetchQueries: [
          {
            query: GetBCContractLinesQuery,
            variables: {
              filter: {
                contractId: { eq: contractId }
              },
              first: ALL_ITEMS
            }
          }
        ],
        onCompleted: () => notificationService.open({
          title: 'Days before renewal',
          text: 'Days before renewal successfully updated',
          type: 'success'
        }),
      }
    );

  const updateContractRenewal = (daysBeforeRenewal: number | null) => {
    _updateContractRenewal({
      variables: {
        contractId,
        daysBeforeRenewal,
      }
    });
  };

  return {
    isLoadingUpdateContractRenewal: loading,
    errorUpdateContractRenewal: error,
    updateContractRenewal
  } as const;
}
